import { useAuth0 } from "@auth0/auth0-react";
import { Box, Button, Typography } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { node_server } from "../../../../../../../../../constants";
import { useTheme } from "../../../../../../../../../context/themeContext";
import {
	creator_withdrew,
	sendProjectApplyConfirmationEmail,
} from "../../../../../../../../../helpers/emails";
import useManageProjects from "../../../../../../../../../hooks/useManageProjects";
import { setIsPageLoading } from "../../../../../../../../../store/slices/loadingSlice";
import { setStatus } from "../../../../../../../../../store/slices/profile/projectDetailsSlice";
import DeleteConfirmation from "./delete-confirmation";
import styles from "./style.module.css";

const Apply = ({ project }) => {
	const { theme, darkMode } = useTheme();
	const dispatch = useDispatch();
	const user = useSelector((state) => state.user.value);
	const { status } = useSelector((state) => state.projectDetails);
	const { fetchApplications } = useManageProjects();
	const { loginWithRedirect } = useAuth0();
	const navigate = useNavigate();
	const { id } = useParams();
	const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

	const handleEditClick = () => {
		navigate(`/projects/${id}/edit`);
	};

	useEffect(() => {
		const fetchData = async () => {
			const response = await axios.get(
				`${node_server}/api/projects/applications/${project.id}`,
				{ withCredentials: true },
			);
			const data = response.data;
			const error = response.status !== 200 ? response.data : null;

			if (error) {
				throw error;
			}

			if (data && data.length > 0) {
				dispatch(setStatus(data[0].status));
			} else {
				dispatch(setStatus(""));
			}
		};

		if (user?.id && project.id) {
			fetchData();
		}
	}, [user, project]);

	const handleApplyClick = async () => {
		const response = await axios.post(
			`${node_server}/api/projects/applications`,
			{
				projectId: project.id
			},
			{ withCredentials: true },
		);
		const error = response.status !== 200 ? response.data : null;
		if (error) console.log(error);

		dispatch(setStatus("Pending"));
		fetchApplications();

		await sendProjectApplyConfirmationEmail(user.id, project.id);
	};

	const handleCancel = async (project) => {
		const response = await axios.delete(
			`${node_server}/api/projects/applications`,
			{
				projectId: project.id,
			},
			{ withCredentials: true },
		);
		const error = response.status !== 200 ? response.data : null;
		if (error) console.log(error);

		dispatch(setStatus(""));

		await creator_withdrew(user?.id, project?.id);
	};

	if (!user) {
		return (
			<Box className={styles.login}>
				<Typography
					variant="body1"
					sx={{
						color: darkMode
							? theme.palette.darkgrey.darkgreybody50
							: theme.palette.darkgrey.darkgrey500,
						fontSize: "18px",
						textAlign: "center",
					}}
				>
					To apply to this project, please
					<span
						onClick={async () => {
							dispatch(setIsPageLoading(true));
							await loginWithRedirect({
								connection: "google-oauth2",
							});
							dispatch(setIsPageLoading(false));
						}}
						style={{
							color: darkMode
								? theme.palette.secondary.blue300
								: theme.palette.secondary.main,
							cursor: "pointer",
						}}
					>
						{" "}
						login{" "}
					</span>
					or
					<span
						onClick={() => {
							navigate("/signup");
						}}
						style={{
							color: theme.palette.secondary.main,
							cursor: "pointer",
						}}
					>
						{" "}
						create an account
					</span>
					.
				</Typography>
			</Box>
		);
	}

	if (user.id === project?.user_id) {
		return (
			<Button variant="contained" fullWidth onClick={handleEditClick}>
				Edit
			</Button>
		);
	}

	return (
		<>
			{project?.status === "Completed" ? (
				<Button
					variant="contained"
					fullWidth
					sx={{
						bgcolor: "#00B0F0",
					}}
				>
					Completed
				</Button>
			) : (
				(status === "Pending" && (
					<Button
						fullWidth
						sx={{
							bgcolor: darkMode
								? "#5C595C"
								: theme.palette.darkgrey.darkgrey500,
							color: theme.palette.lightgrey.lightgrey950,
						}}
						disabled
					>
						Applied
					</Button>
				)) ||
				(status === "" && (
					<Button
						variant="contained"
						fullWidth
						onClick={handleApplyClick}
					>
						Apply Now
					</Button>
				)) ||
				(status === "Accepted" && (
					<Box className={styles.button}>
						<DeleteConfirmation
							isOpen={isCancelModalOpen}
							setIsOpen={setIsCancelModalOpen}
							onDelete={handleCancel}
							project={project}
							text={"cancel"}
						/>
						<Button
							variant="contained"
							fullWidth
							sx={{
								bgcolor: theme.palette.success.dark,
							}}
						>
							Accepted
						</Button>
						<Button
							onClick={() => setIsCancelModalOpen(true)}
							variant="text"
							fullWidth
							sx={{
								color: theme.palette.darkgrey.darkgrey400,
								textTransform: "capitalize",
								fontSize: "16px",
								fontWeight: "500",
								p: "0px",
							}}
						>
							Cancel
						</Button>
					</Box>
				))
			)}
		</>
	);
};

export default Apply;
